"use client";
import { type RefObject, useEffect, useState } from "react";
import { useTranslation } from "@/i18n/client";
import { measureElementSize } from "@/utils/style/measureElementSize";

const OVERFLOW_CLASS_NAME = "informa-nav-sub-menu-position-flipped";

export function useSubmenuPosition(
  ref: RefObject<HTMLElement | null>,
  dependencies: unknown[] = [],
) {
  const [isFlipped, setIsFlipped] = useState(false);
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  useEffect(() => {
    const submenu = ref?.current;
    if (!submenu) return;

    // Checks if submenu would overflow and updates position accordingly
    const updatePosition = () => {
      const parentRect = submenu.parentElement?.getBoundingClientRect();
      if (!parentRect) return;

      const submenuRect = measureElementSize(submenu, {
        removeClasses: [OVERFLOW_CLASS_NAME],
      });
      const viewportWidth = window.innerWidth;

      // Check if menu would overflow
      const wouldOverflow = isRTL
        ? parentRect.left - submenuRect.width < 0 // RTL: left edge overflow
        : parentRect.right + submenuRect.width > viewportWidth; // LTR: right edge overflow

      // Only update if needed to prevent unnecessary rerenders
      if (wouldOverflow !== isFlipped) {
        setIsFlipped(wouldOverflow);

        // Apply or remove class based on overflow
        if (wouldOverflow) {
          submenu.classList.add(OVERFLOW_CLASS_NAME);
        } else {
          submenu.classList.remove(OVERFLOW_CLASS_NAME);
        }
      }
    };

    updatePosition();
    // Important, listening for parent hover to update position
    const parentItem = submenu.parentElement;
    if (parentItem) {
      parentItem.addEventListener("mouseenter", updatePosition);
    }

    return () => {
      if (parentItem) {
        parentItem.removeEventListener("mouseenter", updatePosition);
      }
    };
  }, [ref, isFlipped, isRTL, dependencies]);

  return isFlipped;
}
